'use client';

import { useState } from 'react';
import { useRouter } from 'next/navigation';
import { signIn } from 'next-auth/react';
import { Button } from '@/components/ui/button';
import { motion } from 'framer-motion';

export default function GuestLoginPage() {
  const [nickname, setNickname] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSettingUp, setIsSettingUp] = useState(false);
  const router = useRouter();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const result = await signIn('guest', {
        nickname,
        password,
        redirect: false,
      });

      if (result?.error) {
        setError('Invalid password');
        setIsLoading(false);
      } else if (result?.ok) {
        setIsSettingUp(true);
        router.push('/welcome');
        router.refresh();
        // Keep isLoading and isSettingUp true - they'll reset when page navigates
      }
    } catch (err) {
      setError('An error occurred. Please try again.');
      setIsLoading(false);
    }
  };

  return (
    <div className="flex flex-1 w-full items-center justify-center bg-gradient-to-br from-amber-50/40 via-orange-50/30 to-yellow-50/40 py-8">
      <div className="w-full max-w-md space-y-8 px-4">
        <motion.div className="flex justify-center mb-4">
          <motion.img
            src={`/images/rowmantic-logo-trans.png`}
            alt={'rowmantic.com logo'}
            className="w-[350px] cursor-pointer"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -100 }}
            transition={{
              delay: 0.5,
              exit: { duration: 2.0, delay: 0, ease: [0.4, 0.0, 0.2, 1] },
            }}
          />
        </motion.div>

        <div className="text-center">
          <h2 className="text-3xl font-bold tracking-tight">Guest Access</h2>
          <ul className="mt-2 text-sm text-muted-foreground text-left">
            <li>&bull; Your first name helps to follow up for feedback</li>
            <li>&bull; Guest accounts are temporary</li>
            <li>&bull; Guest accounts cannot be used across devices</li>
          </ul>
        </div>

        <form onSubmit={handleSubmit} className="mt-8 space-y-6">
          <div>
            <label htmlFor="nickname" className="sr-only">
              Name or Nickname
            </label>
            <input
              id="nickname"
              name="nickname"
              type="text"
              required
              autoCapitalize="none"
              className="relative block w-full appearance-none rounded-md border border-input bg-background px-3 py-2 text-foreground placeholder-muted-foreground focus:z-10 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary sm:text-sm"
              placeholder="Your name or nickname"
              value={nickname}
              onChange={(e) => setNickname(e.target.value)}
              disabled={isLoading}
              maxLength={64}
            />
          </div>

          <div>
            <label htmlFor="password" className="sr-only">
              Password
            </label>
            <input
              id="password"
              name="password"
              type="password"
              required
              className="relative block w-full appearance-none rounded-md border border-input bg-background px-3 py-2 text-foreground placeholder-muted-foreground focus:z-10 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary sm:text-sm"
              placeholder="Guest password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={isLoading}
            />
          </div>

          {error && (
            <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
              {error}
            </div>
          )}

          <Button type="submit" className="w-full" disabled={isLoading}>
            {isSettingUp
              ? 'Setting up guest access...'
              : isLoading
                ? 'Signing in...'
                : 'Continue as Guest'}
          </Button>

          <p className="text-center text-xs text-gray-500 dark:text-zinc-400">
            By signing in, you agree to our Terms of Service and{' '}
            <a
              href="/privacy-policy"
              className="underline hover:text-foreground"
            >
              Privacy Policy
            </a>
          </p>

          <div className="text-center text-sm">
            <a
              href="/login"
              className="font-medium text-primary hover:text-primary/80 opacity-0"
            >
              Sign in with an account instead
            </a>
          </div>
        </form>
      </div>
    </div>
  );
}
